/**
 * The printable tracking sheet, rendered into a hidden iframe for
 * `window.print()`. Everything is inline CSS with no app classes, because that
 * iframe loads none of the app's stylesheets.
 */

const BRAND = {
    name: 'SohozKaj',
    url: 'sohozkaj.com',
    primary: '#ea6b0e',
    primaryDark: '#c2560a',
    ink: '#1c1815',
    muted: '#6b635c',
    line: '#ece8e4',
    tint: '#fff4e6',
    surface: '#ffffff',
    page: '#ffffff',
};

/**
 * Colors for the two states the official tracker names. `unknown` is a status
 * code the department has not published a meaning for — it gets a neutral dot
 * and NO label rather than a guess.
 */
const STATE_COLORS = {
    done: { dot: '#1e9e5e', text: '#136c40', tint: '#e3f6ec', border: '#bfe6d1' },
    running: { dot: '#ea6b0e', text: '#a8490a', tint: '#fff4e6', border: '#ffd9ad' },
    unknown: { dot: '#a8a099', text: '#6b635c', tint: '#f7f7f9', border: '#e8e8ee' },
};

/** A4 printable width at 96dpi with 12mm margins. */
export const EXPORT_PAGE_WIDTH_PX = Math.round(((210 - 24) / 25.4) * 96);

export function getBhataTrackingLogoUrl(origin, language = 'bn') {
    const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
    return `${base}/images/${language === 'en' ? 'logo-en.svg' : 'logo.svg'}`;
}

function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function buildSummaryCards(summaryItems) {
    return summaryItems
        .filter(([, value]) => value)
        .map(([label, value]) => `
            <div class="sk-info">
                <span class="sk-info-label">${escapeHtml(label)}</span>
                <span class="sk-info-value">${escapeHtml(value)}</span>
            </div>
        `)
        .join('');
}

function buildTimelineRows(steps, labels) {
    return steps.map((step, index) => {
        const colors = STATE_COLORS[step.stepState] || STATE_COLORS.unknown;
        const isLast = index === steps.length - 1;
        const stamp = [step.date, step.time].filter(Boolean).join(' · ');
        return `
            <li class="sk-step">
                <span class="sk-step-rail">
                    <span class="sk-step-dot" style="background:${colors.dot}"></span>
                    ${isLast ? '' : '<span class="sk-step-line"></span>'}
                </span>
                <span class="sk-step-body">
                    <span class="sk-step-head">
                        <strong class="sk-step-title">${escapeHtml(step.title)}</strong>
                        ${labels.states[step.stepState] ? `
                        <span class="sk-step-chip" style="background:${colors.tint};color:${colors.text};border-color:${colors.border}">
                            ${escapeHtml(labels.states[step.stepState])}
                        </span>
                        ` : ''}
                    </span>
                    ${stamp ? `<span class="sk-step-stamp">${escapeHtml(stamp)}</span>` : ''}
                </span>
            </li>
        `;
    }).join('');
}

/**
 * @param {{
 *   tracking: Record<string, any>,
 *   summaryItems: Array<[string, string]>,
 *   labels: Record<string, any>,
 *   logoUrl?: string,
 * }} params
 */
export function buildBhataTrackingBody({ tracking, summaryItems, labels, logoUrl }) {
    const resolvedLogoUrl = logoUrl || '/images/logo.svg';
    const latest = tracking?.latestStep;
    const latestColors = STATE_COLORS[latest?.stepState] || STATE_COLORS.unknown;

    return `
        <div class="sk-doc">
            <header class="sk-header">
                <div class="sk-brand">
                    <img
                        src="${escapeHtml(resolvedLogoUrl)}"
                        alt="${escapeHtml(BRAND.name)}"
                        class="sk-logo-img"
                        crossorigin="anonymous"
                    />
                </div>
                <div class="sk-header-meta">
                    <div class="sk-doc-title">${escapeHtml(labels.title)}</div>
                    <div class="sk-doc-tagline">${escapeHtml(labels.documentTagline)}</div>
                </div>
            </header>

            <section class="sk-hero">
                <div class="sk-hero-label">${escapeHtml(labels.currentStatus)}</div>
                <div class="sk-hero-status" style="color:${latestColors.text}">
                    ${escapeHtml(latest?.title || '')}
                </div>
                ${tracking?.applicationId ? `
                <span class="sk-hero-id">${escapeHtml(labels.trackingNo)}: ${escapeHtml(tracking.applicationId)}</span>
                ` : ''}
            </section>

            <section class="sk-section">
                <h2 class="sk-section-title">${escapeHtml(labels.applicantDetails)}</h2>
                <div class="sk-info-grid">${buildSummaryCards(summaryItems)}</div>
            </section>

            <section class="sk-section">
                <h2 class="sk-section-title">${escapeHtml(labels.progress)}</h2>
                <ul class="sk-steps">${buildTimelineRows(tracking?.steps || [], labels)}</ul>
            </section>

            <footer class="sk-footer">
                <p class="sk-footer-source">${escapeHtml(labels.officialSource)}</p>
                <p class="sk-footer-disclaimer">${escapeHtml(labels.documentDisclaimer)}</p>
            </footer>
        </div>
    `;
}

export function bhataTrackingStyles() {
    return `
        @page {
            size: A4 portrait;
            margin: 12mm;
        }
        *{box-sizing:border-box;margin:0;padding:0;}
        html,body{background:${BRAND.page};}
        .sk-doc{
            font-family:'Hind Siliguri','Noto Sans Bengali','Segoe UI',system-ui,-apple-system,sans-serif;
            color:${BRAND.ink};
            background:${BRAND.page};
            width:${EXPORT_PAGE_WIDTH_PX}px;
            max-width:${EXPORT_PAGE_WIDTH_PX}px;
            margin:0 auto;
        }
        .sk-header{
            display:flex;align-items:center;justify-content:space-between;gap:16px;
            padding-bottom:16px;border-bottom:3px solid ${BRAND.primary};
            break-inside:avoid;page-break-inside:avoid;
        }
        .sk-logo-img{width:170px;height:auto;max-height:42px;object-fit:contain;object-position:left center;display:block;}
        .sk-header-meta{text-align:right;}
        .sk-doc-title{font-size:15px;font-weight:700;}
        .sk-doc-tagline{font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.08em;color:${BRAND.muted};margin-top:2px;}

        .sk-hero{
            margin-top:18px;text-align:center;padding:20px 16px;
            border:1px solid #ffd9ad;background:linear-gradient(180deg,#fff8ef,${BRAND.tint});
            border-radius:14px;break-inside:avoid;page-break-inside:avoid;
        }
        .sk-hero-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.12em;color:${BRAND.muted};}
        .sk-hero-status{font-size:22px;font-weight:900;margin-top:6px;line-height:1.3;}
        .sk-hero-id{
            display:inline-block;margin-top:10px;padding:5px 14px;border-radius:999px;
            background:#ffffff;border:1px solid #ffd9ad;color:${BRAND.primaryDark};
            font-size:12px;font-weight:800;
        }

        .sk-section{margin-top:18px;break-inside:avoid;page-break-inside:avoid;}
        .sk-section-title{
            font-size:12px;font-weight:800;margin-bottom:10px;padding-left:10px;
            border-left:4px solid ${BRAND.primary};text-transform:uppercase;letter-spacing:0.04em;
            break-after:avoid;page-break-after:avoid;
        }
        .sk-info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
        .sk-info{
            border:1px solid ${BRAND.line};background:${BRAND.surface};border-radius:10px;padding:10px 12px;
            break-inside:avoid;page-break-inside:avoid;
        }
        .sk-info-label{display:block;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#8c847d;}
        .sk-info-value{display:block;margin-top:3px;font-size:13px;font-weight:700;word-break:break-word;}

        .sk-steps{list-style:none;margin:0;padding:0;}
        .sk-step{display:flex;gap:12px;break-inside:avoid;page-break-inside:avoid;}
        .sk-step-rail{position:relative;display:flex;flex-direction:column;align-items:center;width:14px;flex:none;}
        .sk-step-dot{width:12px;height:12px;border-radius:999px;margin-top:4px;flex:none;}
        .sk-step-line{flex:1;width:2px;background:${BRAND.line};margin:4px 0 0;}
        .sk-step-body{display:block;padding-bottom:14px;flex:1;}
        .sk-step-head{display:flex;align-items:center;justify-content:space-between;gap:10px;}
        .sk-step-title{font-size:12.5px;font-weight:800;}
        .sk-step-chip{
            display:inline-block;padding:2px 9px;border-radius:999px;border:1px solid transparent;
            font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:0.05em;white-space:nowrap;
        }
        .sk-step-stamp{display:block;margin-top:3px;font-size:10px;font-weight:700;color:#8c847d;}

        .sk-footer{
            margin-top:22px;padding-top:12px;border-top:1px solid ${BRAND.line};text-align:center;
            break-inside:avoid;page-break-inside:avoid;
        }
        .sk-footer-source{font-size:10px;color:${BRAND.muted};font-weight:600;}
        .sk-footer-disclaimer{margin-top:6px;font-size:9px;line-height:1.55;color:#a8a099;}

        @media print{
            html,body{background:#fff;}
            .sk-doc{width:100%;max-width:100%;background:#fff;}
        }
    `;
}

export function buildBhataTrackingPrintHtml(params) {
    const { labels } = params;
    return `<!DOCTYPE html><html lang="bn"><head>
        <meta charset="utf-8" />
        <title>${escapeHtml(labels.title)}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;600;700&display=swap" rel="stylesheet" />
        <style>${bhataTrackingStyles()}
            body{background:#fff;padding:0;margin:0;}
        </style>
    </head><body>${buildBhataTrackingBody(params)}</body></html>`;
}

